/*
 *   File : basket-select.js
 *   Author : https://evoqins.com
 *   Description : basket select component
 *   Integrations : NA
 *   Version : 1.0.0
 */ 

import React, { useMemo, useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import "../../Style/Components/select.scss";
import { BasketCard } from "../Cards";
import { PrimaryButton } from "../Buttons";

export default function SelectBasket({
  options = [],
  value = null,
  onChange,
  onAdd,
  onPress, // legacy alias
  isClearable = true,
  placeholder = "Select a basket...",
}) {
  // local selected option (object form)
  const [selected, setSelected] = useState(() => {
    if (!value) return null;
    if (
      typeof value === "object" &&
      value !== null &&
      value.value !== undefined
    )
      return value;
    return null;
  });

  // normalize options: ensure `value` (string) and `label` exist and keep original fields
  const normalizedOptions = useMemo(() => {
    return (options || []).map((o, idx) => {
      const nativeId = o?.basket_style_id ?? o?.id ?? o?.value ?? idx;
      const label = o?.title ?? o?.name ?? o?.label ?? String(nativeId);
      return {
        // keep original fields (img, title, CAGR, best_for, risk) for BasketCard
        ...o,
        value: String(nativeId),
        label,
      };
    });
  }, [options]);

  // resolve incoming `value` prop into normalized option object (if possible)
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (
      typeof value === "object" &&
      value !== null &&
      value.value !== undefined
    ) {
      const match = normalizedOptions.find(
        (o) => String(o.value) === String(value.value)
      );
      setSelected(match || value);
      return;
    }
    // scalar id
    const match = normalizedOptions.find(
      (o) => String(o.value) === String(value)
    );
    if (match) setSelected(match);
  }, [value, normalizedOptions]);

  // select change inside the react-select (local only)
  const handleSelectChange = (opt) => {
    setSelected(opt || null);
  };

  // when user presses Add basket in footer
  const handleAdd = (selectProps) => {
    onChange(selected);
    selectProps.onMenuClose();
  };

  // Build BasketCard's expected `data` prop from option object
  const cardDataFromOption = (data) => {
    return {
      id: Number(data.value ?? data.id ?? data.basket_style_id ?? -1),
      img: data.img ?? data.image ?? data.imgUrl,
      title: data.title ?? data.name ?? data.label,
      CAGR: data.CAGR ?? data.return_percentage,
      best_for: data.best_for ?? data.bestFor,
      risk: data.risk ?? data.risk_level,
      return_year:data?.return_year ?? data?.raw?.return_year
    };
  };

  // Custom option cell: icon toggle + BasketCard
  const BasketOption = (optionProps) => {
    const { data } = optionProps;
    const nativeId = String(
      data.value ?? data.basket_style_id ?? data.id ?? ""
    );
    const isSelected = selected && String(selected.value) === nativeId;

    return (
      <components.Option {...optionProps} className="position-relative p-2">
        <div
          className=" e-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setSelected(isSelected ? null : data);
          }}
        >
          <BasketCard
            data={cardDataFromOption(data)}
            type={2}
            isSelected={isSelected}
          />
        </div>
      </components.Option>
    );
  };

  // Menu with Cancel/Add footer
  const MenuWithFooter = (menuProps) => {
    const { innerProps, innerRef, selectProps } = menuProps;
    return (
      <components.Menu {...menuProps}>
        <div
          {...innerProps}
          ref={innerRef}
          className="e-select-menu__menu-list"
        >
          {menuProps.children}
        </div>

        <div className="d-flex justify-content-end align-items-center column-gap-2 px-3 pt-3">
          <PrimaryButton
            ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-220 fs-16 fw-500"
            name="Cancel"
            onPress={() => {
              setSelected(null);
              if (selectProps && typeof selectProps.onMenuClose === "function")
                selectProps.onMenuClose();
            }}
          />

          <PrimaryButton
            ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-220 fs-16 fw-500"
            name="Add basket"
            onPress={() => {
              handleAdd(selectProps), selectProps.onMenuClose();
            }}
            disabled={!selected}
          />
        </div>
      </components.Menu>
    );
  };

  return (
    <div>
      <CreatableSelect
        defaultMenuIsOpen={true}
        isClearable={isClearable}
        isMulti={false}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        className="e-select-basket"
        classNamePrefix="e-select-basket-menu"
        options={normalizedOptions}
        components={{ Option: BasketOption, Menu: MenuWithFooter }}
        value={selected}
        onChange={handleSelectChange}
        placeholder={placeholder}
        getOptionLabel={(o) => o.label}
        getOptionValue={(o) => String(o.value)}
        menuPlacement="auto"
      />
    </div>
  );
}
